import React from 'react';
import { Link } from 'react-router-dom';

const CateringInhomeChefPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <Link
            to="/legal"
            className="text-gray-300 hover:text-white underline"
            aria-label="Back to Legal Center"
          >
            ← Back to Legal Center
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Catering & In-Home Chef Policy
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            This policy applies to any service where a seller (chef, caterer, or culinary team) prepares and/or serves food at a buyer's home, venue, or event location. It is designed to protect both parties, ensure safety, and set clear expectations.
          </p>
          <p className="text-gray-700 mt-4">
            By booking a catering or in-home chef service on Vesta, both buyers and sellers agree to the following terms.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Licensing & Compliance
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers must hold all licenses, permits, and food handler certifications required by their jurisdiction for providing catering or on-site food services.</li>
              <li>Sellers are responsible for compliance with health codes, labor laws, and local business regulations.</li>
              <li>If alcohol is served, sellers must hold the necessary liquor licenses and comply with all related regulations.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Scope of Services
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers must provide a clear description of the menu, service style (e.g., plated, buffet, family-style), staff provided, and event timing in their listing or quote.</li>
              <li>All agreed services and menu items should be documented and confirmed in writing before the event.</li>
              <li>Any additional services requested on-site may incur extra fees and must be approved by the seller before proceeding.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Venue Requirements (Buyer Responsibilities)
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Buyers must provide a clean, safe, and functional kitchen or prep area if required by the seller.</li>
              <li>Access to necessary utilities (water, electricity, refrigeration, cooking equipment) must be confirmed in advance.</li>
              <li>Any known hazards, allergies, or special dietary requirements must be disclosed before the event.</li>
              <li>Buyers must ensure adequate space for setup and service, and provide clear directions for parking and unloading.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Staffing & Conduct
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers and their staff must maintain a professional appearance and demeanor.</li>
              <li>Sellers are responsible for the conduct and safety of their staff while on the buyer's property.</li>
              <li>All staff must follow safe food handling practices at all times.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Insurance
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Vesta strongly recommends sellers carry general liability and/or catering-specific insurance.</li>
              <li>Buyers are encouraged to confirm the seller's insurance coverage prior to booking.</li>
              <li>Neither Vesta nor the buyer is responsible for damages caused by uninsured sellers.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Setup, Service, & Cleanup
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers are responsible for timely arrival, setup, and completion of service.</li>
              <li>Cleanup of the food preparation and service areas must be performed before leaving the venue, unless otherwise agreed.</li>
              <li>Sellers are not responsible for post-event venue cleaning outside of their food service area unless specified in the contract.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Damage & Liability
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers are responsible for any damage caused by themselves or their staff to the buyer's property during the event.</li>
              <li>Buyers are responsible for ensuring their space is safe for staff to operate in.</li>
              <li>Neither party is responsible for damages or injuries caused by hazards beyond their control (e.g., faulty venue infrastructure, acts of nature).</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Cancellations & Refunds
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Cancellation terms must be clearly stated in the seller's listing or service agreement.</li>
              <li>If a buyer cancels within the non-refundable period, the seller may retain all or part of the payment to cover costs.</li>
              <li>If the seller cancels, all payments must be refunded in full unless otherwise agreed by the buyer.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Force Majeure
            </h2>
            <p className="text-gray-700">
              Events outside the control of either party (e.g., severe weather, power outages, natural disasters) may result in cancellation or rescheduling without penalty. Deposits for unstarted work should be refunded; partial refunds may be provided for partially completed work.
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Disputes
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Buyers should raise service-related issues with the seller immediately during or after the event.</li>
              <li>If unresolved within 48 hours, the buyer may escalate the matter to Vesta.</li>
              <li>Vesta reserves the right to issue partial or full refunds, or take other appropriate action, based on evidence provided by both parties.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Food Safety & Allergen Management
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers must follow all applicable food safety guidelines during preparation and service.</li>
              <li>Allergen information must be clearly communicated to buyers and guests.</li>
              <li>Sellers must take reasonable precautions to prevent cross-contamination.</li>
              <li>Emergency contact information should be available in case of allergic reactions.</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Equipment & Supplies
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers must provide all necessary cooking equipment, serving ware, and supplies unless otherwise agreed.</li>
              <li>Equipment must be in good working condition and meet safety standards.</li>
              <li>Sellers should have backup plans for equipment failures.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Guest Count & Dietary Restrictions
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Final guest count must be confirmed at least 48 hours before the event.</li>
              <li>Special dietary requirements must be communicated in advance.</li>
              <li>Sellers should accommodate reasonable dietary restrictions when possible.</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Payment Terms
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Payment schedules must be clearly outlined in the service agreement.</li>
              <li>Deposits are typically non-refundable once preparation begins.</li>
              <li>Final payment is usually due before or immediately after the event.</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about this policy, please contact us at:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: legal@vesta.com</li>
              <li>Catering Support: catering@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Section 16 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              16. Policy Updates
            </h2>
            <p className="text-gray-700">
              We may update this policy to reflect changes in law, industry practices, or Platform operations. Updates will be communicated via email or in-app notifications.
            </p>
          </section>

          {/* Final Statement */}
          <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <p className="text-orange-800 text-center font-medium">
              By booking catering or in-home chef services on Vesta, you acknowledge that you have read, understood, and agree to be bound by this Catering & In-Home Chef Policy.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CateringInhomeChefPolicy; 